import { useState, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { Col, Row, Tab, Table, Tabs, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import GenericList from '~/components/GenericList/GenericList';
import { SingleUserGroupsWidget } from '~/components/GenericForm/Widgets/UserGroupWidget';
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';
import Button from '~/components/CustomButton/CustomButton.jsx';
import UserNotificationSettings from './UserNotificationSettings.js';

//icons
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

//elements
import Select from '~/elements/Select';

//utils
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '~/utils/constants';
import { formatKey } from '~/utils/commonMethods';

const PAGE_SIZE = 10;
const MAX_BULK_ASSIGN_USERS = 10;

const BulkNotificationSettings = ({ t, notificationStore, commonStore, userStore }) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [filters, setFilters] = useState({ name: '', group: '' });
    const [appliedFilters, setAppliedFilters] = useState({ name: '', group: '' });
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [currentPageIds, setCurrentPageIds] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [notificationUser, setNotificationUser] = useState(null);

    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;
    const selectedTemplateIdRef = useRef(selectedTemplateId);
    selectedTemplateIdRef.current = selectedTemplateId;

    const { templates, masterNotifications, loading: templatesLoading } = notificationStore;

    const templateOptions = templates.map((template) => ({
        value: template.id,
        label: template.is_default ? `${template.template_name} (${t('Default')})` : template.template_name,
    }));

    useEffect(() => {
        notificationStore.loadTemplates();
        notificationStore.loadMasterNotifications();
    }, []);

    const fetchUsers = async ({ page = 0, pagesize = PAGE_SIZE, filters } = {}) => {
        const finalFilters = { ...(filters || {}) };
        const currentUser = userStore.currentUser;
        if (currentUser && currentUser.user_type === 'pm' && currentUser.Manages && currentUser.Manages.length) {
            finalFilters.pmMembers = currentUser.Manages.join(',');
        }
        const users = await notificationStore.loadNotificationUsers({ filters: finalFilters, page, pagesize });
        setCurrentPageIds((users.rows || []).map((r) => r.id));
        return users;
    };

    const handleGroupChange = (val) => {
        const updatedFilters = { ...filters, group: val || '' };
        setFilters(updatedFilters);
        setAppliedFilters(updatedFilters);
    };

    const toggleUser = (id) => {
        setSelectedIds((currentIds) => {
            const updatedIds = new Set(currentIds);
            if (updatedIds.has(id)) {
                updatedIds.delete(id);
                return updatedIds;
            }
            if (updatedIds.size >= MAX_BULK_ASSIGN_USERS) {
                commonStore.addNotification(`${t('You can assign a template to a maximum of')} ${MAX_BULK_ASSIGN_USERS} ${t('users at a time')}`, null, 'warning');
                return currentIds;
            }
            updatedIds.add(id);
            return updatedIds;
        });
    };

    const isAllOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));

    const toggleSelectAll = () => {
        if (!selectedTemplateId) {
            commonStore.addNotification(t('Please select a notification template first.'), null, 'warning');
            return;
        }
        setSelectedIds((currentIds) => {
            const updatedIds = new Set(currentIds);
            if (isAllOnPageSelected) {
                currentPageIds.forEach((id) => updatedIds.delete(id));
                return updatedIds;
            }
            const idsToAdd = currentPageIds.filter((id) => !updatedIds.has(id));
            const remainingSlots = MAX_BULK_ASSIGN_USERS - updatedIds.size;
            if (idsToAdd.length > remainingSlots)
                commonStore.addNotification(`${t('You can assign a template to a maximum of')} ${MAX_BULK_ASSIGN_USERS} ${t('users at a time')}`, null, 'warning');
            idsToAdd.slice(0, remainingSlots).forEach((id) => updatedIds.add(id));
            return updatedIds;
        });
    };

    const selectedTemplate = templates.find((template) => String(template.id) === String(selectedTemplateId));
    const templateSettings = (selectedTemplate && selectedTemplate.settings) || {};

    const getNotificationSettings = (key) => {
        const settings = templateSettings[key];
        if (!settings) return { allow_push: false, allow_email: false, allow_system: false, is_obligatory: false };
        return settings;
    };

    const handleAssign = () => {
        if (!selectedTemplateId || selectedIds.size === 0) return;
        confirmAlert({
            title: t('Are You Sure'),
            message: `${t('Assign')} "${selectedTemplate?.template_name}" ${t('to')} ${selectedIds.size} ${t('user(s)')}? ${t('This will overwrite their current notification settings.')}`,
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        setAssigning(true);
                        try {
                            await notificationStore.bulkAssignNotificationTemplate({ template_id: parseInt(selectedTemplateId, 10), user_ids: Array.from(selectedIds) });
                            commonStore.addNotification(t('Notification template assigned successfully.'), null, 'success');
                            setSelectedIds(new Set());
                        } catch (e) {
                            commonStore.addNotification(e?.message || t('Something went wrong'), null, 'error');
                        } finally {
                            setAssigning(false);
                        }
                    },
                },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    const columns = [
        {
            Header: 'Select',
            id: 'select',
            width: 140,
            textAlign: 'start',
            sortable: false,
            Cell: ({ row }) => {
                const isSelected = selectedIdsRef.current.has(row.original.id);
                const limitReached = !isSelected && selectedIdsRef.current.size >= MAX_BULK_ASSIGN_USERS;
                const checkbox = (
                    <Checkbox
                        inlineBlock
                        isChecked={isSelected}
                        disabled={limitReached}
                        onChange={() => {
                            if (!selectedTemplateIdRef.current) {
                                commonStore.addNotification(t('Please select a notification template first.'), null, 'warning');
                                return;
                            }
                            toggleUser(row.original.id);
                        }}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                );
                return (
                    <div className="d-flex align-items-start justify-content-start ps-3">
                        {limitReached ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id={`bulk-limit-tooltip-${row.original.id}`}>
                                        {`${t('You can assign a template to a maximum of')} ${MAX_BULK_ASSIGN_USERS} ${t('users at a time')}`}
                                    </Tooltip>
                                }
                            >
                                <span className="d-inline-block">{checkbox}</span>
                            </OverlayTrigger>
                        ) : checkbox}
                    </div>
                );
            },
        },
        {
            Header: t('Name'),
            id: 'name',
            textAlign: 'start',
            width: 180,
            Cell: ({ row }) => `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim(),
        },
        {
            Header: t('Actions'),
            id: 'actions',
            width: 100,
            sortable: false,
            Cell: ({ row }) => (
                <OverlayTrigger placement="top" overlay={<Tooltip id={`user-notif-tooltip-${row.original.id}`}>{t('Notification Settings')}</Tooltip>}>
                    <Button
                        icon_sm fill
                        onClick={() => setNotificationUser({
                            id: row.original.id,
                            name: `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim(),
                        })}
                    >
                        <i className="fa fa-bell text-primary-color" />
                    </Button>
                </OverlayTrigger>
            ),
        },
    ];

    return (
        <div>
            <Row className="g-0">
                <Col xs={6} className="border-end">
                    <div className="mb-2 pt-3">
                        <Row>
                            <Col xs={8}>
                                <label className="control-label">{t('Select a Notification Template')}</label>
                                <Select
                                    options={templateOptions}
                                    value={templateOptions.find((o) => o.value === Number(selectedTemplateId)) || null}
                                    onChange={(opt) => setSelectedTemplateId(opt ? String(opt.value) : '')}
                                    isClearable
                                    isLoading={templatesLoading}
                                />
                            </Col>
                        </Row>
                        <p className="mb-0 mt-2 font-size-12 text-muted fst-italic">
                            <strong>
                                {t('Note')}: {t("Assigning a new template will overwrite all current notification settings for the selected users. You can still customize individual user settings from the user list.")}
                            </strong>
                        </p>
                    </div>

                    {selectedTemplateId ? (
                        <Tabs defaultActiveKey={NOTIFICATION_CATEGORIES[0]} id="BnsTemplateTabs" className="mt-2">
                            {NOTIFICATION_CATEGORIES.map((category) => (
                                <Tab key={category} eventKey={category} title={t(NOTIFICATION_CATEGORY_LABELS[category])}>
                                    <div className="overflow-auto" style={{ maxHeight: "380px" }}>
                                        <Table hover responsive className="mb-0">
                                            <thead>
                                                <tr className="font-size-12">
                                                    <th className="text-start">{t('Notification')}</th>
                                                    <th className="text-center">{t('Push')}</th>
                                                    <th className="text-center">{t('Email')}</th>
                                                    <th className="text-center">{t('System')}</th>
                                                    <th className="text-center">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`obligatory-tooltip-${category}`}>
                                                                    {t("If enabled, this notification will be sent regardless of the user's notification preferences.")}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span>{t('Obligatory')}</span>
                                                        </OverlayTrigger>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(masterNotifications[category] || []).map((notification) => {
                                                    const settings = getNotificationSettings(notification.key);
                                                    return (
                                                        <tr key={notification.id}>
                                                            <td className="text-start">
                                                                <div className="font-size-13">{formatKey(notification.key)}</div>
                                                                {notification.description && <small className="text-muted">{notification.description}</small>}
                                                            </td>
                                                            <td className="text-center">
                                                                {settings.allow_push ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                                            </td>
                                                            <td className="text-center">
                                                                {settings.allow_email ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                                            </td>
                                                            <td className="text-center">
                                                                {settings.allow_system ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
                                                            </td>
                                                            <td className="text-center">
                                                                {settings.is_obligatory ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-muted" />}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>
                            ))}
                        </Tabs>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center p-5 mt-3 text-muted">
                            <i className="fa fa-bell-slash fa-2x mb-3 opacity-50" />
                            <span className="font-size-13">{t('Select a template to preview its notification settings.')}</span>
                        </div>
                    )}
                </Col>

                <Col xs={6} className="ps-2 pt-3">
                    <GenericList
                        columns={columns}
                        requestData={fetchUsers}
                        filters={appliedFilters}
                        pageSize={PAGE_SIZE}
                        header={
                            <Row className="g-1 align-items-center">
                                <Col md={2}>
                                    <div className="d-flex align-items-center gap-1">
                                        <Checkbox
                                            inlineBlock
                                            isChecked={isAllOnPageSelected}
                                            onChange={toggleSelectAll}
                                            wrapperClassName="checkbox-position-fix"
                                            labelClassName="ps-0"
                                        />
                                        <small className="text-muted">{t('Select All')}</small>
                                    </div>
                                </Col>

                                <Col md={4}>
                                    <SingleUserGroupsWidget
                                        value={filters.group}
                                        onChange={handleGroupChange}
                                    />
                                </Col>

                                <Col md={6}>
                                    <div className="d-flex conversation-search">
                                        <input
                                            type="text"
                                            className="conversation-search-input"
                                            placeholder={`${t('Search')} ${t('Employee')}`}
                                            value={filters.name}
                                            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') setAppliedFilters(filters);
                                            }}
                                        />
                                        <i
                                            className="conversation-search-icon fa fa-search cursor-pointer"
                                            onClick={() => setAppliedFilters(filters)}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        }
                    />
                </Col>
            </Row>

            <div className="d-flex align-items-center justify-content-end p-3 border-top position-sticky">
                {selectedIds.size > 0 && (
                    <Button fill dynamic_lg onClick={handleAssign} disabled={!selectedTemplateId || assigning} >
                        {assigning ? t('Saving...') : `${t('Assign Template')} (${selectedIds.size})`}
                    </Button>
                )}
            </div>

            <Modal
                size="lg"
                className="right modal_revised stacked-modal-1060-zindex"
                dialogClassName="modal-dialog-stacked"
                backdropClassName="stacked-modal-backdrop-1060-zindex"
                show={!!notificationUser}
                onHide={() => setNotificationUser(null)}
            >
                <Modal.Header>
                    <Modal.Title className="w-100 text-center m-0">
                        {t('Notification Settings')}
                    </Modal.Title>
                    <img
                        src={CloseModalIcon}
                        className="cursor-pointer"
                        onClick={() => setNotificationUser(null)}
                        alt={'cross-Icon'}
                    />
                </Modal.Header>
                <Modal.Body style={{ minHeight: '80vh' }}>
                    {!!notificationUser && (
                        <UserNotificationSettings
                            userId={notificationUser.id}
                            userName={notificationUser.name}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default inject('notificationStore', 'commonStore', 'userStore')(withLocalization(observer(BulkNotificationSettings)));
